import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, X } from 'lucide-react';
import { LeadDetailView } from '@/pages/LeadDetailPage';

// Right-side drawer showing the full lead detail, spanning from the right
// edge to the app sidebar (desktop) or full width (mobile, sidebar hidden).
export function LeadDetailDrawer({ leadId, onClose, onChanged }: {
  leadId: string;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const navigate = useNavigate();
  const [left, setLeft] = useState(0);

  // Track the sidebar's right edge (collapsing/expanding or resizing moves it).
  useEffect(() => {
    const measure = () => {
      const el = document.querySelector('[data-app-sidebar]') as HTMLElement | null;
      setLeft(el ? Math.max(0, el.getBoundingClientRect().right) : 0);
    };
    measure();
    const el = document.querySelector('[data-app-sidebar]');
    const ro = el && 'ResizeObserver' in window ? new ResizeObserver(measure) : null;
    if (el && ro) ro.observe(el);
    window.addEventListener('resize', measure);
    return () => { ro?.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  // Escape closes (only when no modal on top is handling it).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-y-0 right-0 z-40 flex flex-col bg-[#F8F9FA] shadow-2xl border-l border-slate-200 animate-in slide-in-from-right duration-300"
      style={{ left }}
    >
      <div className="flex h-12 sm:h-14 flex-shrink-0 items-center justify-end gap-3 border-b border-slate-100 bg-white px-3 sm:px-6">
        <button
          type="button"
          onClick={() => navigate(`/leads/${leadId}`)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#6366F1] hover:underline"
        >
          <ExternalLink className="h-4 w-4" /> Open full page
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6">
        <LeadDetailView key={leadId} leadId={leadId} embedded onClose={onClose} onChanged={onChanged} />
      </div>
    </div>
  );
}
